import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

interface User {
  id: string;
  username: string;
  has_shiphero_key: boolean;
  has_apparelmagic_key: boolean;
  default_integration: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

  // Refresh token function - returns true if successful, false otherwise
  const refreshToken = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        console.log('Token refreshed successfully');
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      // If refresh fails with 401, the refresh token is expired - need to re-login
      if (error.response?.status === 401 && error.response?.data?.require_login) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        setUser(null);
        navigate('/login');
      }
      return false;
    }
  }, [API_URL, navigate]);

  // Set up periodic token refresh (every 30 minutes)
  useEffect(() => {
    if (user) {
      // Start periodic refresh - no immediate call needed since tokens are fresh after login
      refreshIntervalRef.current = setInterval(() => {
        refreshToken();
      }, 30 * 60 * 1000); // 30 minutes
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [user, refreshToken]);

  // Set up axios interceptor to handle 401 errors and attempt token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // If we get a 401 and haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== `${API_URL}/auth/refresh`) {
          originalRequest._retry = true;
          
          const refreshed = await refreshToken();
          if (refreshed) {
            // Retry the original request
            return axios(originalRequest);
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [API_URL, refreshToken]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password
      });

      const { token, has_shiphero_key, has_apparelmagic_key, default_integration } = response.data;
      localStorage.setItem('token', token);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser({
        id: response.data.user_id || '',
        username: response.data.username,
        has_shiphero_key,
        has_apparelmagic_key: has_apparelmagic_key || false,
        default_integration: default_integration || 'shiphero'
      });

      toast.success('Login successful!');
      navigate('/');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Login failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      // Continue with logout even if API call fails
    }

    localStorage.removeItem('token');
    localStorage.removeItem('selectedIntegration'); // Clear old localStorage key
    sessionStorage.removeItem('sessionIntegration'); // Clear session selection
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
